import type { CardWalletsTranslations } from "../interfaces/cardWallets";

export const cardWalletsTranslations: CardWalletsTranslations = {
  page_title: "Card wallet",
  page_subtitle:
    "Credit or correct the USD balance an organization loads its cards from. Every movement is recorded with its reference.",
  refresh_button: "Refresh",
  refresh_aria: "Refresh the balance and the movements",
  action_retry: "Retry",
  field_organization: "Organization",
  field_organization_required_hint: "Choose the organization whose balance you are moving.",
  field_organization_hint: "Choosing it opens its card wallet.",
  org_picker_placeholder: "Search by name, email or id…",
  org_picker_loading: "Loading organizations…",
  org_picker_empty: "We found no organization.",
  org_picker_searching: "Searching…",
  org_picker_search_error: "We could not search organizations. Try again.",
  org_picker_catalog_error: "We could not load the list of organizations.",
  org_picker_catalog_truncated: "There are more organizations than the list holds: search by name or id.",
  landing_title: "Choose an organization to see its card wallet",
  landing_body:
    "Here you will see the USD balance it loads its cards from, its movement history, and you will be able to credit or correct it. Only organizations whose cards run on Coloca Crypto can have their balance adjusted.",

  wallet_balance_label: "Available balance",
  wallet_loading: "Loading the balance",
  wallet_pending: "Pending {{amount}}",
  wallet_id_label: "Wallet",
  wallet_id_copied: "We copied the wallet id.",
  wallet_id_copy_action: "Copy the wallet id",
  wallet_status_active: "Active",
  wallet_status_frozen: "Frozen",
  wallet_status_suspended: "Suspended",
  wallet_status_closed: "Closed",
  wallet_status_notice: "This wallet is {{status}}. The movement will be recorded anyway.",
  wallet_just_created: "This organization had no card wallet; we just opened an empty one for it.",
  wallet_error_title: "We could not read the balance",
  wallet_error_body:
    "Without the balance we cannot show you the before and after of a movement, so the actions stay disabled.",
  action_credit: "Credit balance",
  action_debit: "Debit balance",
  actions_footnote: "This cannot be undone. To correct it you have to record the opposite movement.",

  program_crypto: "Coloca Crypto",
  program_cards: "Coloca Cards",
  program_none: "No card program",
  ineligible_bloque:
    "This organization's cards run on Coloca Cards. Its balance is managed by the provider and is not adjusted from here.",
  ineligible_no_program: "This organization has no card program yet, so there is no balance to adjust.",
  ineligible_currency: "This wallet is not in USD, so it cannot be adjusted from here.",
  ineligible_wallet_type: "This wallet is not the organization's card wallet.",

  dialog_title_credit: "Credit balance",
  dialog_title_debit: "Debit balance",
  dialog_description_credit: "Record a USD deposit already confirmed in Coloca's treasury.",
  dialog_description_debit: "Record a correction that lowers this organization's card wallet balance.",
  stepper_label: "Movement steps",
  step_data: "Details",
  step_review: "Review",
  step_result: "Result",
  mode_banner_credit: "You are crediting balance to {{name}} · Card wallet · USD",
  mode_banner_debit: "You are debiting balance from {{name}} · Card wallet · USD",
  field_reference: "Deposit reference",
  field_reference_hint:
    "The identifier you will reconcile with: deposit id, ticket number or batch. It cannot be repeated on this wallet, and it is the only text left for whoever reads this later.",
  field_reference_placeholder: "E.g.: DEP-2026-10-14-ACME",
  field_reference_count: "{{used}} / 120",
  field_amount: "Amount",
  field_amount_hint: "In USD, with at most 2 decimals.",
  action_cancel: "Cancel",
  action_review: "Review",
  action_back: "Back",

  validation_reference: "Enter the reference you will reconcile this movement with.",
  validation_reference_long: "The reference cannot exceed 120 characters.",
  validation_amount: "Enter an amount greater than zero.",
  validation_amount_precision: "The amount takes at most 2 decimals.",
  validation_amount_over_balance:
    "The available balance is {{balance}}; it is not enough to debit {{amount}}.",

  review_heading_credit: "You are crediting balance to {{name}}",
  review_heading_debit: "You are debiting balance from {{name}}",
  review_organization: "Organization",
  review_wallet: "Wallet",
  review_wallet_value: "Cards · USD · {{short}}",
  review_balance_before: "Balance before",
  review_movement: "Movement",
  review_balance_after: "Balance after",
  review_reference: "Reference",
  review_client_sees:
    'In the organization\'s history it will appear as "Manual balance adjustment", with the date and the amount. The client does not see the reference you wrote.',
  review_webhook_credit:
    "If the organization has a webhook configured, we notify it of the credit. We send no email and no in-app notification.",
  review_webhook_debit:
    "A debit notifies nobody: the organization will see a lower balance with no explanation. Tell them through the appropriate channel.",
  review_irreversible:
    "This cannot be undone. To correct it you have to record the opposite movement, which stays as a second entry in the history.",
  review_checkbox_credit: "I confirm the USD deposit is already confirmed in Coloca's treasury.",
  review_checkbox_debit: "I confirm I must withdraw this balance and that I already told the organization.",
  review_balance_moved:
    "The balance changed while you were reviewing: it is now {{balance}}. Check the result before confirming.",
  action_confirm_credit: "Record the credit · {{amount}}",
  action_confirm_debit: "Record the debit · {{amount}}",

  done_label: "Movement recorded",
  done_pill_credit: "Credited",
  done_pill_debit: "Debited",
  done_amount: "Amount",
  done_balance_before: "Balance before",
  done_balance_after: "Balance after",
  action_another: "Record another movement",
  action_close: "Close",

  error_zero: "Enter an amount greater than zero.",
  error_currency: "This wallet is not in USD, so it cannot be adjusted from here.",
  error_ineligible:
    "This organization's cards run on Coloca Cards and its balance is managed by the provider. It cannot be adjusted from here.",
  error_insufficient: "The available balance is {{balance}}; it is not enough to debit {{amount}}.",
  error_not_found: "We could not find this wallet. It may have been closed while you were working.",
  error_not_found_action: "Search for the organization again",
  error_duplicate_title: "This reference was already used",
  error_duplicate_body:
    'A movement with the reference "{{reference}}" already exists on this wallet. The money moved once, on the first attempt. Do not repeat it.',
  error_duplicate_see: "See the movement",
  error_duplicate_change: "Change the reference",
  error_duplicate_off_page:
    "The movement is not on this page of the history. We cleared the filters so you can find it.",
  error_rate_limited: "Only one movement can be recorded per minute. Wait a moment and try again.",
  error_rate_limited_retry: "Retry in {{seconds}} s",
  error_rate_limited_ready: "You can try again now.",
  error_permission:
    "We could not authorize the movement. Only staff authorized to move money can adjust balances; if you should be able to, ask to be added.",
  error_unconfirmed_title: "We do not know whether it was recorded",
  error_unconfirmed_body:
    "We got no response from the server. The movement may well have been recorded. Do not repeat it with the same reference until you check.",
  error_unconfirmed_check: "Check",
  error_unconfirmed_not_placed: "It was not recorded. You can try again with the same reference.",
  error_unconfirmed_still: "We still cannot check it. Try again in a moment.",
  error_generic: "We could not record the movement. Try again.",

  history_title: "Movements of this wallet",
  col_date: "Date",
  col_movement: "Movement",
  col_reference: "Reference",
  col_by: "Recorded by",
  col_amount: "Amount",
  badge_manual_adjustment: "Manual adjustment",
  by_you: "You",
  by_unavailable: "Not available",
  by_unavailable_card: "Author not available on this entry",
  by_unavailable_tooltip:
    "This kind of entry does not return who made it. It is stored in the backend, but it does not reach this screen.",
  filter_type: "Type",
  filter_type_all: "All",
  filter_direction: "Direction",
  filter_direction_all: "All",
  filter_direction_in: "Incoming",
  filter_direction_out: "Outgoing",
  filter_dates: "Date range",
  filter_clear: "Clear filters",
  empty_title: "This wallet has no movements yet",
  empty_description:
    "The movements you record here, and the organization's top-ups and transfers, will appear in this list.",
  empty_filtered_title: "No movements match these filters",
  history_error: "We could not load the movements.",

  tx_type_topup: "Top-up",
  tx_type_transfer: "Transfer",
  tx_type_withdrawal: "Withdrawal",
  tx_type_fee: "Fee",
  tx_type_deposit: "Deposit",
  tx_type_reversal: "Reversal",
  tx_type_adjustment: "Adjustment",
  tx_status_pending: "Pending",
  tx_status_completed: "Completed",
  tx_status_failed: "Failed",
  tx_status_reversed: "Reversed",

  tx_desc_manual_adjustment: "Manual balance adjustment",
  tx_desc_wallet_recharge: "Wallet recharge",
  tx_desc_card_crypto_deposit: "Crypto deposit to the card",
  tx_desc_card_crypto_transfer: "Moved to the card for a crypto deposit",
  tx_desc_internal_transfer: "Transfer between wallets",

  quote_button: "Quote a recharge",
  quote_title: "Recharge quoter",
  quote_description:
    "Work out how much a client has to deposit and how much lands, and look up the wallet they deposit to. It moves no money and records nothing.",
  quote_close: "Close",

  quote_network_label: "Blockchain",
  quote_address_label: "Wallet address",
  quote_address_copy_aria: "Copy the wallet address",
  quote_address_copied: "We copied the wallet address.",
  quote_assets_label: "Accepted assets",
  quote_asset_contract: "Contract: {{address}}",
  quote_pool_warning:
    "Only {{assets}} on {{network}}. Any other asset, or the same one on another network, is lost and cannot be recovered.",
  quote_pool_error: "We could not read the deposit wallet. Without it we cannot tell you where to send.",
  quote_pool_retry: "Retry",

  quote_mode_label: "What you know about the amount",
  quote_mode_deposit_title: "The client pays",
  quote_mode_deposit_description: "You know what they will deposit and want to know what gets credited.",
  quote_mode_credited_title: "The client wants to recharge",
  quote_mode_credited_description: "You know what must end up credited and want to know what to deposit.",
  quote_amount_label_deposit: "How much does the client pay?",
  quote_amount_label_credited: "How much do they want to recharge?",
  quote_amount_hint: "In USD, with at most 2 decimals.",
  quote_commission_label: "Commission",
  quote_commission_unlock: "Adjust the commission",
  quote_commission_hint: "{{percent}} % by default. Tick the box to change it for this quote only.",
  quote_issue_amount_invalid: "Enter an amount greater than zero.",
  quote_issue_commission_invalid: "The commission must be between 0 % and 100 %.",
  quote_result_pays: "The client pays",
  quote_result_deposit: "They must deposit",
  quote_result_commission: "Commission ({{percent}} %)",
  quote_result_credited: "Gets credited",
  quote_rounding_note: "We round up to the cent, so {{amount}} gets credited.",
  quote_empty: "Enter an amount to see the quote.",
  quote_footnote:
    "This is a reference calculation only: it reserves and records nothing. When the deposit arrives, credit the balance from this screen with its reference.",
  two_factor_required:
    "Adjusting balances needs two-step verification turned on. Turn it on in Settings and come back.",
  two_factor_description_credit: "You are about to credit {{amount}} to {{name}}.",
  two_factor_description_debit: "You are about to debit {{amount}} from {{name}}.",
};
